export type RunbookRef =
  | {
      kind: 'param';
      name: string;
      source: string;
    }
  | {
      kind: 'output';
      name: string;
      source: string;
    }
  | {
      kind: 'step_output';
      stepId: string;
      outputKey: string;
      /**
       * Dot-path into a structured output, e.g. `@step.s.reviewOutputs.csv` →
       * outputKey `reviewOutputs`, path `['csv']`. Empty/absent for a whole-output
       * ref. Lets a downstream step pull a single field out of an object output
       * (e.g. an `ai.reason` Object response) instead of the whole JSON blob.
       */
      path?: string[];
      source: string;
    }
  | {
      // RFC-194 §8.7 — trigger system context: @event.<field>. Plus @run.<field>
      // — the current run's own metadata (id, procedure, start time) so later
      // steps can reference it. Reserved namespaces, not declared params;
      // recognized by authoring validators + autocomplete so they aren't flagged
      // as unknown refs. (CES scope is NOT a namespace — it binds into @params.)
      kind: 'system';
      namespace: 'event' | 'run';
      field: string;
      source: string;
    };

/** Reserved system-context namespaces (RFC-194 §8.7). */
export const RUNBOOK_SYSTEM_NAMESPACES = ['event', 'run'] as const;

/**
 * RFC-194 §8.6 — the reserved id of the runbook's single **Start** node (the
 * entry point that holds the input contract + triggers). A runbook graph must
 * have **one and only one** Start node: it is injected exactly once at render
 * time and is NOT a runbook step, so no step may use this id (doing so would
 * shadow or duplicate the Start). Validators reject a step with this id.
 */
export const RUNBOOK_START_NODE_ID = '__start__';

/**
 * RFC-194 §8.6 — the reserved id of the runbook's single **Output** node (the
 * terminal result/evidence marker that leaf steps flow into). Like Start, it is
 * injected exactly once at render time and is NOT a runbook step, so no step may
 * use this id. Validators reject a step with this id.
 */
export const RUNBOOK_OUTPUT_NODE_ID = '__output__';

/** True if `id` is the reserved Output node id (so it can't be used by a step). */
export function isRunbookOutputNodeId(id: string): boolean {
  return id === RUNBOOK_OUTPUT_NODE_ID;
}

/**
 * True if `id` is one of the reserved injected node ids (Start or Output), so no
 * runbook step may use it.
 */
export function isReservedRunbookNodeId(id: string): boolean {
  return id === RUNBOOK_START_NODE_ID || id === RUNBOOK_OUTPUT_NODE_ID;
}

/**
 * RFC-194 §8.7 — the EXACT fields each system namespace exposes at run time.
 * Single source of truth shared by authoring validators (web + server) and the
 * runtime context builders (`build{Event,Cadence}SystemContext`). A field not
 * listed here resolves to `undefined` at run time, so authoring must reject it
 * — notably `@event.payload` is intentionally ABSENT (PII rides declared params,
 * never the expression context).
 */
export const RUNBOOK_SYSTEM_FIELDS: Record<
  'event' | 'run',
  readonly string[]
> = {
  event: ['eventType', 'subjectId'],
  // The run's own metadata — always available (derived from the execution row),
  // so it resolves on the first step and on resume alike.
  run: ['id', 'procedureId', 'procedureName', 'startedAt', 'executedBy', 'workspaceId'],
};

/** True iff `@<namespace>.<field>` is a real run-time system-context field. */
export function isKnownSystemField(
  namespace: 'event' | 'run',
  field: string,
): boolean {
  return RUNBOOK_SYSTEM_FIELDS[namespace].includes(field);
}

const RUNBOOK_PARAM_REF_RE = /@params\.([A-Za-z_][\w-]*)/g;
const RUNBOOK_OUTPUT_REF_RE = /@step\.([A-Za-z_][\w-]*)(?![\w.-])/g;
// `@step.<stepId>.<outputKey>(.<field>)*` — the trailing group captures a
// dot-path into a structured output (e.g. `.csv` / `.summaryMd`).
const RUNBOOK_STEP_OUTPUT_REF_RE =
  /@step\.([A-Za-z0-9_-]+)\.([A-Za-z_][\w-]*)((?:\.[A-Za-z_][\w-]*)*)/g;
const RUNBOOK_SYSTEM_REF_RE = /@(event|run)\.([A-Za-z_][\w-]*)/g;

/**
 * The charset a minted id must use to be referenceable. These mirror the ref
 * grammar above so a freshly-minted step/output id is guaranteed to parse +
 * resolve as `@step.<stepId>.<outputId>` — no colons, and an output id starts
 * with a letter/underscore (never a digit). Mint ids through {@link slugifyRefId}
 * and assert with these to keep the whole class of "ref breaks at a bad char"
 * bugs out of the codebase.
 */
export const REF_SAFE_STEP_ID_RE = /^[A-Za-z0-9_-]+$/;
export const REF_SAFE_OUTPUT_ID_RE = /^[A-Za-z_][\w-]*$/;

export const isRefSafeStepId = (id: string): boolean =>
  REF_SAFE_STEP_ID_RE.test(id);
export const isRefSafeOutputId = (id: string): boolean =>
  REF_SAFE_OUTPUT_ID_RE.test(id);

/**
 * Slug a human label into a ref-safe output id (`@step.<id>.<thisId>`): lower
 * snake_case, leading char forced to a letter/underscore (the grammar forbids a
 * leading digit), empty → `field`. e.g. "2FA setup" → `_2fa_setup`, "Notes" →
 * `notes`.
 */
export function slugifyRefId(label: string): string {
  const base = label
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
  if (!base) return 'field';
  return /^[a-z_]/.test(base) ? base : `_${base}`;
}

/** Split a captured `.a.b` suffix into `['a','b']` (empty for no suffix). */
function parseRefPath(suffix: string | undefined): string[] {
  return suffix ? suffix.split('.').filter(Boolean) : [];
}

export function parseRunbookRef(value: string): RunbookRef | null {
  const trimmed = value.trim();
  const paramMatch = /^@params\.([A-Za-z_][\w-]*)$/.exec(trimmed);
  if (paramMatch) {
    return {
      kind: 'param',
      name: paramMatch[1],
      source: trimmed,
    };
  }

  const outputMatch = /^@step\.([A-Za-z_][\w-]*)$/.exec(trimmed);
  if (outputMatch) {
    return {
      kind: 'output',
      name: outputMatch[1],
      source: trimmed,
    };
  }

  const stepMatch =
    /^@step\.([A-Za-z0-9_-]+)\.([A-Za-z_][\w-]*)((?:\.[A-Za-z_][\w-]*)*)$/.exec(
      trimmed,
    );
  if (stepMatch) {
    const path = parseRefPath(stepMatch[3]);
    return {
      kind: 'step_output',
      stepId: stepMatch[1],
      outputKey: stepMatch[2],
      ...(path.length > 0 ? { path } : {}),
      source: trimmed,
    };
  }

  const systemMatch = /^@(event|run)\.([A-Za-z_][\w-]*)$/.exec(trimmed);
  if (systemMatch) {
    return {
      kind: 'system',
      namespace: systemMatch[1] as 'event' | 'run',
      field: systemMatch[2],
      source: trimmed,
    };
  }

  return null;
}

export function extractRunbookRefs(value: string): RunbookRef[] {
  const refs: RunbookRef[] = [];

  RUNBOOK_PARAM_REF_RE.lastIndex = 0;
  let paramMatch: RegExpExecArray | null;
  while ((paramMatch = RUNBOOK_PARAM_REF_RE.exec(value)) !== null) {
    refs.push({
      kind: 'param',
      name: paramMatch[1],
      source: paramMatch[0],
    });
  }

  RUNBOOK_OUTPUT_REF_RE.lastIndex = 0;
  let outputMatch: RegExpExecArray | null;
  while ((outputMatch = RUNBOOK_OUTPUT_REF_RE.exec(value)) !== null) {
    refs.push({
      kind: 'output',
      name: outputMatch[1],
      source: outputMatch[0],
    });
  }

  RUNBOOK_STEP_OUTPUT_REF_RE.lastIndex = 0;
  let stepMatch: RegExpExecArray | null;
  while ((stepMatch = RUNBOOK_STEP_OUTPUT_REF_RE.exec(value)) !== null) {
    const path = parseRefPath(stepMatch[3]);
    refs.push({
      kind: 'step_output',
      stepId: stepMatch[1],
      outputKey: stepMatch[2],
      ...(path.length > 0 ? { path } : {}),
      source: stepMatch[0],
    });
  }

  RUNBOOK_SYSTEM_REF_RE.lastIndex = 0;
  let systemMatch: RegExpExecArray | null;
  while ((systemMatch = RUNBOOK_SYSTEM_REF_RE.exec(value)) !== null) {
    refs.push({
      kind: 'system',
      namespace: systemMatch[1] as 'event' | 'run',
      field: systemMatch[2],
      source: systemMatch[0],
    });
  }

  return refs;
}

/**
 * Legacy `{{name}}` / `{{ name }}` / `{{params.name}}` / `{{input.name}}`
 * mustache placeholder — the format the autowire + some model output still emit.
 * The capture groups yield the bare name and an optional dotted sub-path.
 */
const MUSTACHE_TOKEN_RE = /\{\{\s*([A-Za-z_][\w-]*)((?:\.[\w-]+)*)\s*\}\}/g;

/**
 * Rewrite legacy `{{…}}` mustache placeholders to canonical `@`-refs so they
 * render as chips in the runbook editor and read consistently with the rest of
 * the ref grammar. Resolution at run time is unaffected — the executor handles
 * both the `@`-ref and the legacy `{{…}}` fallback.
 *
 * Conversion is intentionally CONSERVATIVE — only WHOLE-token refs are rewritten:
 *   `{{name}}`         → `@params.name` (name is a declared param)
 *                      → `@step.name`   (name is an upstream output)
 *   `{{params.name}}`  → `@params.name` (only when name is a declared param)
 *   `{{input.name}}`   → `@params.name` (only when name is a declared param)
 *
 * A dotted sub-path on an output (`{{out.text}}`, `{{diff.csv.onlyInLeft}}`) is
 * LEFT AS-IS: the `@`-ref grammar + authoring validator only express
 * `@step.<outputName>` (no sub-path) — `@step.<name>.<key>` would be misread as
 * `stepId.outputKey` and fail ref-validation. Those keep resolving via the
 * executor's legacy `{{…}}` substitution.
 */
export function normalizeMustacheRefs(
  value: string,
  ctx: {
    paramNames: ReadonlySet<string>;
    outputNames: ReadonlySet<string>;
  },
): string {
  return value.replace(
    MUSTACHE_TOKEN_RE,
    (match, name: string, subpath: string) => {
      // Explicit `params.`/`input.` prefix with a single trailing key → param,
      // but only when that param is actually declared (parity with the bare-name
      // branch — never fabricate a ref to an undeclared param).
      if (
        (name === 'params' || name === 'input') &&
        /^\.[A-Za-z_][\w-]*$/.test(subpath)
      ) {
        const key = subpath.slice(1);
        return ctx.paramNames.has(key) ? `@params.${key}` : match;
      }
      // Any other dotted sub-path stays legacy (see doc comment).
      if (subpath) return match;
      if (ctx.paramNames.has(name)) return `@params.${name}`;
      if (ctx.outputNames.has(name)) return `@step.${name}`;
      // Unknown bare name — can't safely fabricate a ref; leave it untouched.
      return match;
    },
  );
}

export function formatRunbookRef(ref: { [K in RunbookRef['kind']]: Omit<Extract<RunbookRef, { kind: K }>, 'source'> }[RunbookRef['kind']]): string {
  if (ref.kind === 'param') {
    return `@params.${ref.name}`;
  }
  if (ref.kind === 'output') {
    return `@step.${ref.name}`;
  }
  if (ref.kind === 'system') {
    return `@${ref.namespace}.${ref.field}`;
  }

  return `@step.${ref.stepId}.${ref.outputKey}`;
}
